import { EventType, UserType } from "globals/types/models.ts";
import Modal from "components/atom/Modal.tsx";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "config/store";
import CategoryIcon from "components/atom/CategoryIcon.tsx";
import Icon from "components/atom/Icon.tsx";
import Button from "components/atom/button/Button.tsx";
import { authActions } from "config/store/modules/auth.ts";
import { globalActions } from "config/store/modules/global.ts";
import { getUrlFromFormat } from "globals/functions/image.ts";
import { searchActions } from "config/store/modules/search.ts";
import { useTranslation } from "react-i18next";
import { LANGS, USER_TYPES } from "globals/constants.ts";
import { useSingleEventData } from "utils/data/event.ts";
import { Helmet } from "react-helmet";
import { getEventSchema } from "utils/seo/event.ts";
import { OG_DEFAULT_IMAGE_URL } from "utils/data/global.ts";
import { EnumEventStatValueType } from "globals/types/globals.ts";
import { useMount } from "react-use";
import ConfirmActionModal from "components/modals/ConfirmActionModal.tsx";
import { useNavigate } from "react-router-dom";
import EventDateListItem from "components/layouts/event/EventDateListItem.tsx";

function SingleEventModal(props: { event: EventType | null, onClose: () => void }) {
  const { event, onClose } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const selectedDay: string | null = useAppSelector(state => state.search.selectedDay);
  const favoriteEventIds: number[] = useAppSelector(state => state.auth.favoriteEventIds);
  const statsRef = useRef<{ singleModalSent: boolean }>({ singleModalSent: false });
  const [eventWasJustSignaled, setEventWasJustSignaled] = useState(false);
  const [showNeedToBeLoginModal, setShowNeedToBeLoginModal] = useState<boolean>(false);
  const [needToLoginModal, setNeedToLoginModal] = useState<"favorite" | "add_to_calendar" | null>(null);

  const currentUser: UserType | null = useAppSelector(state => state.auth.user);

  const dispatch = useAppDispatch();
  const currentLang = useAppSelector(state => state.global.currentLang);

  const {
    category,
    upcomingDate,
    pastDate,
    fullAddress,
    googleMapsDirections
  } = useSingleEventData(event, selectedDay);


  useEffect(() => {
    if (!event) return;
    if (statsRef.current.singleModalSent) return;
    statsRef.current.singleModalSent = true;//prevent multiple call
    dispatch(authActions.statCollectionEventByUuid(event.uuid, "view", "single_modal"));
  }, [dispatch, event]);

  useMount(async () => {
    if (!event) return;
    if (!currentUser) return;//need to be logged in
    if (currentUser.type === USER_TYPES.EVENT_MANAGER) return;//cant favorite event manager
    dispatch(authActions.checkIfEventIsFavorite(event.id));
  });

  const isFavorite = useMemo(() => {
    if (!event) return false;
    return favoriteEventIds.includes(event.id);
  }, [event, favoriteEventIds]);

  const onBtnClick = useCallback((btnName: EnumEventStatValueType) => {
    return (e: any) => {
      if (!event) return;
      dispatch(authActions.statCollectionEventByUuid(event.uuid, "btn_click", btnName));

      //so if it crash the writeText crash, we still have the stat
      if (btnName === "share_url") {
        e.preventDefault();
        navigator.clipboard.writeText(window.location.href);
        const title = event.titleFr ? event.titleFr : event.titleEn ? event.titleEn : event.titleFr;
        if (title) {
          try {
            navigator.share({
              url: window.location.href,
              title: title,
              text: t("event.shareText")
            });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Failed to share:", err);
          }
        }
        dispatch(globalActions.setToast({
          type: "success",
          message: t("event.linkCopied")
        }));
      }
    };
  }, [t, dispatch, event]);

  const toggleFavorite = useCallback(() => {
    if (!event) return;
    if (!currentUser) {
      dispatch(authActions.statCollectionEventByUuid(event.uuid, "btn_click", "not_connected_favorite"));
      setShowNeedToBeLoginModal(true);
      setNeedToLoginModal("favorite");
      return;
    }
    if (currentUser.type === USER_TYPES.EVENT_MANAGER) {
      //cant favorite event manager
      return;
    }
    dispatch(authActions.statCollectionEventByUuid(event.uuid, "btn_click", "favorite"));

    if (isFavorite) {
      dispatch(authActions.removeEventFavorite(event.id));
    } else {
      dispatch(authActions.makeEventFavorite(event.id));
    }
  }, [currentUser, dispatch, event, isFavorite]);

  const addToCalendar = useCallback((e: any) => {
    if (!event) return;
    if (!currentUser) {
      dispatch(authActions.statCollectionEventByUuid(event.uuid, "btn_click", "not_connected_add_to_calendar"));
      e.preventDefault();
      setShowNeedToBeLoginModal(true);
      setNeedToLoginModal("add_to_calendar");
      return;
    }
    dispatch(authActions.statCollectionEventByUuid(event.uuid, "btn_click", "add_to_calendar"));
  }, [event, currentUser, dispatch]);

  const signalEvent = useCallback(() => {
    if (!event) return;
    setEventWasJustSignaled(true);
    dispatch(searchActions.signalEventByUuid(event.uuid));
  }, [dispatch, event]);


  const imageUrl = useMemo(() => {
    if (!event) return null;
    const firstImage = event.images.length > 0 ? event.images[0] : null;
    let imageUrl = firstImage ? firstImage.imageUrl : null;
    if (firstImage && imageUrl) {
      if (firstImage.formats) {
        if (firstImage.formats.length > 0) {
          imageUrl = getUrlFromFormat(imageUrl, firstImage.formats, 1440);
        }
      }
    }
    return imageUrl;
  }, [event]);

  const icsUrl = useMemo(() => {
    if (!event) return null;
    if (!upcomingDate) return null;
    return `${process.env.API_URL}/api/v1/public/events/uuid/${event.uuid}.ics`;
  }, [event, upcomingDate]);

  if (!event) {
    return null;
  }

  const showFavoriteBtn = !currentUser || currentUser.type === USER_TYPES.VIEWER;
  const showAddToCalendar = icsUrl && (!currentUser || currentUser.type === USER_TYPES.VIEWER);

  const eventSeoSchema = getEventSchema(event, currentLang);
  const eventTitle = currentLang === LANGS.FR ? event.titleFr : event.titleEn ? event.titleEn : event.titleFr;
  const eventDescription = currentLang === LANGS.FR ? event.descriptionFr : event.descriptionEn ? event.descriptionEn : event.descriptionFr;


  return (
    <Fragment>

      <Helmet>
        <title>{eventTitle}</title>
        <meta name="description" content={eventDescription || ""} />
        <meta property="og:title" content={eventTitle || ""} />
        <meta property="og:description" content={eventDescription || ""} />
        <meta property="og:image" content={imageUrl || OG_DEFAULT_IMAGE_URL} />
        <meta property="og:image:secure_url" content={imageUrl || OG_DEFAULT_IMAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={currentLang} />
        {/*    <meta property="og:image:width" content="1200"/>
                <meta property="og:image:height" content="630"/> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={eventTitle || ""} />
        <meta name="twitter:description" content={eventDescription || ""} />
        <meta name="twitter:image" content={imageUrl || OG_DEFAULT_IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(eventSeoSchema)}</script>
      </Helmet>

      <Modal className="-single-event" onClose={onClose}
             btnRightChildren={(
               <Fragment>
                 {showFavoriteBtn && (
                   <div
                     onClick={toggleFavorite}
                     className="bg-white rounded-full shadow p-2 pointer-events-auto outline-0 cursor-pointer border-none"
                   >
                     <Icon className={`h-5 min-w-5 w-5 ${isFavorite ? "text-green-500" : "text-black"}`}
                           name={isFavorite ? "heart" : "heart-outline"} />
                   </div>
                 )}
               </Fragment>
             )}
      >
        <div className="flex h-full flex-col items-start justify-start relative">

          {imageUrl ? (
            <div className="relative h-[250px] min-h-[250px] w-full overflow-hidden">
              <img alt={`BeThere | Event | ${eventTitle}`} src={imageUrl}
                   className="absolute inset-0 z-1 block h-full w-full object-contain object-center" />
              <img alt={`BeThere | Event | ${eventTitle}`} src={imageUrl}
                   className="pointer-events-none absolute inset-0 z-0 block h-full w-full object-cover object-center"
                   style={{ filter: "blur(6px) brightness(0.8)", transform: "scale(1.1)" }}
              />
            </div>
          ) : (
            <div className="relative h-[250px] min-h-[250px] w-full overflow-hidden bg-black" />
          )}

          <div className="flex w-full flex-1 flex-col items-start justify-start p-3 sm:p-6 relative">
            <div className="hidden md:block absolute right-2 top-2">
              <SignalButton eventWasJustSignaled={eventWasJustSignaled} signalEvent={signalEvent} />
            </div>

            <div className="w-full flex-1 space-y-4">
              <div className="flex flex-row items-center justify-start gap-4">
                {category && (
                  <div
                    className="flex h-12 w-12 min-w-12 flex-col items-center justify-center rounded-full bg-green-150 p-3">
                    <CategoryIcon handle={category.handle}
                                  className="block h-full w-full text-green-500" />
                    <span className="sr-only">
                      {category.titleFr}
                    </span>
                  </div>
                )}
                <div className="sm:mr-16">
                  <h1 className="text-lg font-semibold">
                    {eventTitle}
                  </h1>
                  {pastDate && (
                    <h3 className="text-xs font-medium text-red-400">
                      {t("event.pastEvent")}
                    </h3>
                  )}
                </div>
              </div>
              <div className="">
                <h2 className="text-base font-medium">
                  {t("event.details")}
                </h2>
                <ul className="space-y-1 pt-1">
                  {upcomingDate ? (
                    <EventDateListItem date={upcomingDate} />
                  ) : (
                    <span>
                      {pastDate && (
                        <EventDateListItem date={pastDate} />
                      )}
                    </span>
                  )}
                  {fullAddress && (
                    <li className="w-full flex flex-row items-center justify-start gap-1">
                      <dl
                        className="w-full flex flex-row items-center justify-start gap-2"
                      >
                        <dt
                          className="flex flex-row items-center justify-start gap-1 text-base text-gray-400 font-normal w-20">
                          <Icon name="map-pin" className="h-4 w-4 min-w-4 text-gray-400" />
                          <span className="text-xs">
                            {t("event.destination")}
                          </span>
                        </dt>
                        <dd className="text-base text-gray-600 flex-1">
                          {googleMapsDirections ? (
                            <a href={googleMapsDirections} target="_blank"
                               onClick={onBtnClick("directions")}
                               className="text-base text-green-700 underline cursor-pointer">
                              {fullAddress}
                            </a>
                          ) : (
                            <p className="text-base text-gray-600">
                              {fullAddress}
                            </p>
                          )}
                        </dd>
                      </dl>
                    </li>
                  )}
                  {event.price && (
                    <li className="w-full flex flex-row items-center justify-start gap-1">
                      <dl
                        className="w-full flex flex-row items-center justify-start gap-2"
                      >
                        <dt
                          className="flex flex-row items-center justify-start gap-1 text-base text-gray-400 font-normal w-20">
                          <Icon name="dollar-sign" className="h-4 w-4 min-w-4 text-gray-400" />
                          <span className="text-xs">
                            {t("event.price")}
                          </span>
                        </dt>
                        <dd className="text-base text-gray-600 flex-1">
                          {event.price}
                        </dd>
                      </dl>
                    </li>
                  )}
                </ul>
              </div>
              <div className="">
                <h2 className="text-base font-medium">
                  {t("event.description")}
                </h2>
                <p className="whitespace-pre-wrap pt-1 lg:pr-6 text-gray-600">
                  {eventDescription}
                </p>
              </div>
              <div className="inline-block md:hidden pt-2">
                <SignalButton eventWasJustSignaled={eventWasJustSignaled} signalEvent={signalEvent} />
              </div>


              <div className="pt-6">
                <h2 className="text-base font-medium">
                  {t("event.actions")}
                </h2>
                <div className="flex flex-row flex-wrap items-center justify-start gap-3 pt-1 lg:gap-6">
                  {event.phoneNumber && (
                    <a href={`tel:${event.phoneNumber}`}
                       target="_blank"
                       aria-label={t("event.call")}
                       onClick={onBtnClick("phone")}
                       className="flex appearance-none flex-row items-center justify-center gap-3 rounded-full bg-green-150 px-6 py-3">
                      <Icon name="phone" className="h-4 w-4 min-w-4 text-green-500" />
                      <span className="text-base font-medium">
                        {t("event.call")}
                      </span>
                    </a>
                  )}
                  {event.website && (
                    <a href={event.website}
                       target="_blank"
                       aria-label={t("event.visitWebsite")}
                       onClick={onBtnClick("website")}
                       className="flex appearance-none flex-row items-center justify-center gap-3 rounded-full bg-green-150 px-6 py-3">
                      <Icon name="globe" className="h-4 w-4 min-w-4 text-green-500" />
                      <span className="text-base font-medium">
                        {t("event.visitWebsite")}
                      </span>
                    </a>
                  )}
                  {showAddToCalendar && (
                    <a href={icsUrl}
                       target="_blank"
                       aria-label={t("event.addToCalendar")}
                       onClick={addToCalendar}

                       className="appearance-none flex cursor-pointer flex-row items-center justify-center gap-3 rounded-full bg-green-150 px-6 py-3">
                      <Icon name="calendar" className="h-4 w-4 min-w-4 text-green-500" />
                      <span className="text-base font-medium">
                        {t("event.addToCalendar")}
                      </span>
                    </a>
                  )}
                  <button type="button" onClick={onBtnClick("share_url")}
                          aria-label={t("event.shareLink")}
                          className="appearance-none flex cursor-pointer flex-row items-center justify-center gap-3 rounded-full bg-green-150 px-6 py-3">
                    <Icon name="share" className="h-4 w-4 min-w-4 text-green-500" />
                    <span className="text-base font-medium">
                      {t("event.shareLink")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
            {event.externalLink && (
              <div className="w-full pt-12">
                <Button className="w-full"
                        ariaLabel={t("event.reserve")}
                        target="_blank" href={event.externalLink}
                        onClick={onBtnClick("external")}>
                  {t("event.reserve")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmActionModal isOpen={showNeedToBeLoginModal}
                          showTitle={false}
                          acceptLabel={t("auth.login")}
                          onClose={() => {
                            setShowNeedToBeLoginModal(false);
                            setNeedToLoginModal(null);
                          }}
                          onAccept={() => {
                            setShowNeedToBeLoginModal(false);
                            setNeedToLoginModal(null);
                            onClose();
                            navigate({ pathname: "/map", hash: "#/auth/login" });
                          }}>
        <h3 className="text-center">
          {needToLoginModal === "favorite" && (
            <span>
              {t("event.needToBeLoginToFavorite")}
            </span>
          )}
          {needToLoginModal === "add_to_calendar" && (
            <span>
              {t("event.needToBeLoginToAddToCalendar")}
            </span>
          )}
        </h3>
      </ConfirmActionModal>
    </Fragment>
  );
}


function SignalButton(props: { eventWasJustSignaled: boolean, signalEvent: () => void }) {
  const { eventWasJustSignaled, signalEvent } = props;
  const { t } = useTranslation();
  return (
    <Fragment>
      {eventWasJustSignaled ? (
        <p
          className="px-2 py-1 rounded bg-yellow-100 text-yellow-500 text-xxs flex flex-row justify-center items-center gap-2">
          {t("event.signaled")}
        </p>
      ) : (
        <button type="button" onClick={signalEvent}
                className="appearance-none px-2 py-1 rounded bg-red-100 text-red-500 text-xxs cursor-pointer flex flex-row justify-center items-center gap-2">
          <Icon name="alert-circle" className="h-4 w-4 min-w-4" />
          {t("event.signal")}
        </button>
      )}
    </Fragment>
  );
}


export default SingleEventModal;
